import { defineStore } from 'pinia'

import axios from '@axios'
import {reportError, extractDomain} from '@/plugins/misc'
import { parseContactEntries } from '@/plugins/contactEntries'
import localforage from 'localforage'
import { useUsersStore } from './users.store'

const baseUrl = `/restaurant/`

// Dedupe a list by _id (falling back to id), keeping first occurrence and order.
// Discover's prefetch + load-more paths can race and re-fetch an overlapping
// page; deduping on append guarantees a restaurant never renders twice. Exported
// so the component's direct-assignment append sites can apply the same guard.
export function dedupeById(list) {
  const seen = new Set()
  const out = []
  for (const item of list) {
    const key = item && (item._id ?? item.id)
    if (key == null) {
      out.push(item)
      continue
    }
    const k = String(key)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(item)
  }

  return out
}

function formatMarket(addressComponents) {
  let city = null
  let state = null
  let country = null

  addressComponents.forEach(component => {
    if (component.types.includes('locality')) {
      city = component.long_name
    }
    if (component.types.includes('administrative_area_level_1')) {
      state = component.short_name
    }
    if (component.types.includes('country')) {
      country = component.long_name
    }
  })

  if (!city) {
    addressComponents.forEach(component => {
      if (component.types.includes('sublocality')) {
        city = component.long_name
      }
    })
  }

  if (!city) {
    addressComponents.forEach(component => {
      if (component.types.includes('postal_town')) {
        city = component.long_name
      }
    })
  }
  if (city) {
    if (state && country === 'United States') {
      return `${city}, ${state}`
    } else if (country) {
      return `${city}, ${country}`
    } else {
      return city
    }
  } else {
    if (state && country) {
      return `${city}, ${state}`
    } else if (country) {
      return `${country}`
    } else {
      return null
    }
  }
}

// Track in-flight friends-favorited-batch requests to prevent duplicate concurrent calls
const friendsFetchedIds = new Set()

export const useRestaurantsStore = defineStore({
  id: 'restaurants',
  state: () => ({
    restaurants: [], //JSON.parse(localStorage.getItem('restaurants')) || [],
    bookedRestaurantsSearch: [],
    bookedRestaurants: [],
    preferredRestaurants: [],
    favoritedRestaurants: [],
    friendsFavoriteRestaurants: [],
    friendsFavoritedByMap: {},
    eventSpacesRestaurants: [],
    happeningsRestaurants: [],
    restaurantSearchResults: [], //JSON.parse(localStorage.getItem('restaurants')) || [],
    restaurantByNameAndMarket: {},
    markets: JSON.parse(localStorage.getItem('markets')) || [],
    marketObject: {},
    timeZones: null,
    groups: null,
    preferredMarkets: [],
    preferredAndFavoriteOnlyMarkets: [],
    totalPreferredCount: 0,
    allBookingMarkets: [], // Stable list of all markets that have bookings
    citySelectorData: null, // Backend-provided city selector data
    favoriteNotes: {},
    // Restaurant notes on places the member has NOT favorited — shown as rows under
    // Favorites → Notes (Founder review, 2026-09-17). General notes with no restaurant
    // are NOT here: they belong to the Dining Profile.
    unfavoritedNotes: [],
    adminEditingRestaurant: null,
    adminEditingRestaurantPrevious: null,
    restaurantEditingRestaurant: null,
    restaurantEditingRestaurantPrevious: null,
    viewingDetailsRestaurant: null,
    viewingDetailsRestaurantTab: 'Details',
    viewingDetailsRestaurantShowBookings: false,
    isInitialized: false,
    hasFetchedRestaurantList: false,
  }),
  actions: {
    async loadInitialData() {
      if (this.isInitialized) {
        return  // If already initialized, do nothing
      }

      // Clear any stale cached restaurant data from localforage
      // Restaurant data should always be fetched fresh to avoid stale hold settings
      localforage.removeItem('restaurants')
      localStorage.removeItem('restaurants')

      this.isInitialized = true
    },

    //getMarkets - not used right now, but maybe later
    async getMarkets() {
      try {
        const { data } = await axios.get(`${baseUrl}markets`)
        this.markets = data.sort((a, b) => a.localeCompare(b))
        localStorage.setItem('markets', JSON.stringify(this.markets))
      } catch (error) {
        console.log('err', error)
        reportError('restaurant.store.js:getMarkets', error.message, null, null, null, error)
      }
    },

    async searchRestaurants(textSearch, market = null, timeZoneIdentifier = null) {
      try {
        const params = {}
        if (textSearch) params.textSearch = textSearch
        if (market) params.market = market
        if (timeZoneIdentifier) params.timeZoneIdentifier = timeZoneIdentifier
        const { data } = await axios.get(baseUrl, { params })

        //make copy of data
        this.restaurantSearchResults = JSON.parse(JSON.stringify(data))

        // Add displayName and displayNameAndMarket to each restaurant (needed for v-combobox item-value)
        this.restaurantSearchResults.forEach(restaurant => {
          if (!restaurant.acceptingReferrals) {
            restaurant.displayName = restaurant.name + '**'
            restaurant.displayNameAndMarket = restaurant.name + '**' + ' - ' + restaurant.market
          }
          else if (restaurant.status == 'Member Favorite') {
            restaurant.displayName = restaurant.name + '*'
            restaurant.displayNameAndMarket = restaurant.name + '*' + ' - ' + restaurant.market
          }
          else if (restaurant.status == 'Preferred') {
            restaurant.displayName = restaurant.name
            restaurant.displayNameAndMarket = restaurant.name + ' - ' + restaurant.market
          }
          else {
            restaurant.displayName = restaurant.name + '***'
            restaurant.displayNameAndMarket = restaurant.name + '***' + ' - ' + restaurant.market
          }
        })

      } catch (error) {
        console.log('err', error)
        reportError('restaurant.store.js:restaurantSearchResults', error.message, null, null, null, error)
      }
    },

    async getRestaurants(sortBy = 'createdAt', sortOrder = 'desc') {
      try {
        const params = {
          sortBy,
          sortOrder
        }
        const { data } = await axios.get(baseUrl, { params })

        // Backend now handles sorting, so just assign the data directly
        this.restaurants = data
        this.hasFetchedRestaurantList = true
        const marketObject = {}
        const preferredMarketCounts = {}
        const bookedMarketCounts = {}
        const groups = new Set()
        const markets = new Set()
        const timeZones = new Set()
        const preferredMarkets = new Set()
        const preferredAndFavoriteOnlyMarkets = new Set()

        // LOOP through restaurants and build the marketObject, groups, markets, timeZones, and restaurantByNameAndMarket
        this.restaurants.forEach(restaurant => {

          this.restaurantByNameAndMarket[restaurant.name + '-' + restaurant.market] = restaurant

          if (marketObject[restaurant.market]) {
            marketObject[restaurant.market].count++
          }
          else {
            marketObject[restaurant.market] = { count: 1 }
          }

          // Count preferred restaurants by market
          if (restaurant.status === 'Preferred') {
            preferredMarketCounts[restaurant.market] = (preferredMarketCounts[restaurant.market] || 0) + 1
          }

          timeZones.add(restaurant.timeZoneIdentifier)
          if (restaurant.group)
            groups.add(restaurant.group)
          if (restaurant.market && restaurant.market != 'Chicago, IL') {
            markets.add(restaurant.market)
            if (restaurant.status === 'Preferred') {
              preferredMarkets.add(restaurant.market)
            }
            if (restaurant.status === 'Preferred' || restaurant.status === 'Member Favorite') {
              preferredAndFavoriteOnlyMarkets.add(restaurant.market)
            }
          }
          if (!restaurant.acceptingReferrals) {
            restaurant.displayName = restaurant.name + '**'
            restaurant.displayNameAndMarket = restaurant.name + '**' + ' - ' + restaurant.market
          }
          else if (restaurant.status == 'Member Favorite') {
            restaurant.displayName = restaurant.name + '*'
            restaurant.displayNameAndMarket = restaurant.name + '*' + ' - ' + restaurant.market
          }
          else if (restaurant.status == 'Preferred') {
            restaurant.displayName = restaurant.name
            restaurant.displayNameAndMarket = restaurant.name + ' - ' + restaurant.market
          }
          else {
            restaurant.displayName = restaurant.name + '***'
            restaurant.displayNameAndMarket = restaurant.name + '***' + ' - ' + restaurant.market
          }
        })
        this.timeZones = [...Array.from(timeZones).sort((a, b) => a.localeCompare(b))]
        this.groups = [...Array.from(groups).sort((a, b) => a.localeCompare(b))]

        //this.markets = an array of marketObject keys be sorted by favoriteCount and then alphabetically
        this.markets = Object.keys(marketObject).sort((a, b) => {
          if (marketObject[a].count === marketObject[b].count) {
            return a.localeCompare(b)
          }
          
          return marketObject[b].count - marketObject[a].count
        })
        this.marketObject = marketObject

        // this.markets = ['Chicago, IL', ...Array.from(markets).sort((a, b) => a.localeCompare(b))];

        // Build preferredMarkets array with counts for city selector
        const preferredMarketsWithCounts = ['Chicago, IL', ...Array.from(preferredMarkets).sort((a, b) => a.localeCompare(b))]
          .map(market => {
            const count = preferredMarketCounts[market] || 0
            
            return count > 0 ? `${market} (${count})` : market
          })

        this.preferredMarkets = preferredMarketsWithCounts
        this.preferredAndFavoriteOnlyMarkets = ['Chicago, IL', ...Array.from(preferredAndFavoriteOnlyMarkets).sort((a, b) => a.localeCompare(b))]

        // Store total preferred count for stable "All Cities" display
        this.totalPreferredCount = this.restaurants.filter(r => r.status === 'Preferred').length

        localStorage.setItem('markets', JSON.stringify(this.markets))
      } catch (error) {
        console.log('err', error)
        reportError('restaurant.store.js:getRestaurants', error.message, null, null, null, error)
      }
    },

    async getDefaultSearchData(limit) {
      try {
        let res = await axios.get(`${baseUrl}search-data`, {
          params: {
            limit
          }
        })
        this.bookedRestaurantsSearch = res.data
      } catch (error) {
        console.error('Error Default Booked', error)
        reportError('restaurant.store.js:getDefaultBookedRestaurants', error.message, null, null, null, error)
      }
    },

    async getBookedRestaurants(limit, combined, market = null, skip = 0, append = false, fetchOnly = false, occasion = null) {
      // JWT is now sent automatically via cookies (no need to pass in params)
      const params = { limit, combined, skip }
      if (market) params.market = market
      if (occasion) params.occasion = occasion
      try {
        const { data } = await axios.get(`${baseUrl}booked`, { params })
        
        // If fetchOnly, just return the data without updating store (for prefetching)
        if (fetchOnly) {
          return data
        }
        
        // Append to existing results if specified, otherwise replace
        if (append) {
          this.bookedRestaurants = dedupeById([...this.bookedRestaurants, ...data])
        } else {
          this.bookedRestaurants = data
        }

        // Build stable booking markets list only from unfiltered calls
        if (!market && !append && data && data.length > 0) {
          const bookingMarkets = new Set()
          data.forEach(booking => {
            if (booking.raw?.market) {
              bookingMarkets.add(booking.raw.market)
            }
          })
          this.allBookingMarkets = Array.from(bookingMarkets).sort((a, b) => a.localeCompare(b))
        }
        
        return data // Return data for length checking
      } catch (error) {
        console.error('Error Booked', error)
        reportError('restaurant.store.js:getBookedRestaurants', error.message, null, null, null, error)
        
        return []
      }
    },

    async getPreferredRestaurants(limit, market = null, skip = 0, append = false, fetchOnly = false, sort = null) {
      try {
        // JWT is now sent automatically via cookies (no need to pass in params)
        const params = { limit, skip }
        if (market) params.market = market
        if (sort) params.sort = sort // 'popular' = shared trending score

        let res = await axios.get(`${baseUrl}preferred`, { params })

        // If fetchOnly, just return the data without updating store (for prefetching)
        if (fetchOnly) {
          return res.data
        }

        // Append to existing results if specified, otherwise replace
        if (append) {
          this.preferredRestaurants = dedupeById([...this.preferredRestaurants, ...res.data])
        } else {
          this.preferredRestaurants = res.data
        }

        return res.data // Return data for length checking
      } catch (error) {
        console.error('Error Preferred', error)
        reportError('restaurant.store.js:getPreferredRestaurants', error.message, null, null, null, error)
        
        return []
      }
    },

    async getFavoritedRestaurants(limit, market = null, skip = 0, append = false, fetchOnly = false, sort = null) {
      try {
        // JWT is now sent automatically via cookies (no need to pass in params)
        const params = { limit, skip }
        if (market) params.market = market
        if (sort) params.sort = sort // 'alpha' for A-Z sorting

        let res = await axios.get(`${baseUrl}favorited`, { params })

        // If fetchOnly, just return the data without updating store (for prefetching)
        if (fetchOnly) {
          return res.data
        }

        // Append to existing results if specified, otherwise replace
        if (append) {
          this.favoritedRestaurants = dedupeById([...this.favoritedRestaurants, ...res.data])
        } else {
          this.favoritedRestaurants = res.data
        }

        return res.data // Return data for length checking
      } catch (error) {
        console.error('Error Favorited', error)
        reportError('restaurant.store.js:getFavoritedRestaurants', error.message, null, null, null, error)
        
        return []
      }
    },

    async getFriendsFavoriteRestaurants(limit, market = null, skip = 0, append = false, fetchOnly = false) {
      try {
        const params = { limit, skip }
        if (market) params.market = market

        let res = await axios.get(`${baseUrl}friends-favorites`, { params })

        if (fetchOnly) {
          return res.data
        }

        if (append) {
          this.friendsFavoriteRestaurants = dedupeById([...this.friendsFavoriteRestaurants, ...res.data])
        } else {
          this.friendsFavoriteRestaurants = res.data
        }

        return res.data
      } catch (error) {
        console.error('Error Friends Favorites', error)
        reportError('restaurant.store.js:getFriendsFavoriteRestaurants', error.message, null, null, null, error)

        return []
      }
    },

    clearFriendsFavoritedCache() {
      friendsFetchedIds.clear()
      this.friendsFavoritedByMap = {}
    },

    async fetchFriendsFavoritedBatch(restaurantIds) {
      if (!restaurantIds || restaurantIds.length === 0) return
      try {
        // Skip IDs already fetched or in-flight to avoid duplicate API calls
        const newIds = restaurantIds.filter(id => !friendsFetchedIds.has(id))
        if (newIds.length === 0) return

        // Mark IDs as in-flight
        newIds.forEach(id => friendsFetchedIds.add(id))

        // Backend caps at 50 IDs per request, so batch into chunks of 50
        const BATCH_SIZE = 50
        const chunks = []
        for (let i = 0; i < newIds.length; i += BATCH_SIZE) {
          chunks.push(newIds.slice(i, i + BATCH_SIZE))
        }

        const results = await Promise.all(
          chunks.map(chunk => {
            const ids = chunk.join(',')
            
            return axios.get(`${baseUrl}friends-favorited-batch`, { params: { ids } })
          })
        )

        // Merge results into existing map using direct assignment
        // to avoid race conditions when multiple concurrent fetches run in parallel
        results.forEach(({ data }) => {
          for (const [key, value] of Object.entries(data)) {
            this.friendsFavoritedByMap[key] = value
          }
        })
      } catch (error) {
        // On failure, remove IDs from fetched set so they can be retried
        restaurantIds.forEach(id => friendsFetchedIds.delete(id))
        console.error('Error fetching friends favorited batch', error)
      }
    },

    async getCitySelectorData() {
      try {
        // JWT is now sent automatically via cookies (no need to pass in params)
        const { data } = await axios.get(`${baseUrl}city-selector`)
        this.citySelectorData = data
        
        return data
      } catch (error) {
        console.error('Error fetching city selector data', error)
        reportError('restaurant.store.js:getCitySelectorData', error.message, null, null, null, error)
        throw error
      }
    },

    async getFavoritedCitySelectorData() {
      try {
        // JWT is now sent automatically via cookies (no need to pass in params)
        const { data } = await axios.get(`${baseUrl}favorited-city-selector`)
        
        return data
      } catch (error) {
        console.error('Error fetching favorited city selector data', error)
        reportError('restaurant.store.js:getFavoritedCitySelectorData', error.message, null, null, null, error)
        throw error
      }
    },

    async getBookedCitySelectorData() {
      try {
        // JWT is now sent automatically via cookies (no need to pass in params)
        const { data } = await axios.get(`${baseUrl}booked-city-selector`)
        
        return data
      } catch (error) {
        console.error('Error fetching booked city selector data', error)
        reportError('restaurant.store.js:getBookedCitySelectorData', error.message, null, null, null, error)
        throw error
      }
    },

    async getSingleRestaurant(name, market) {
      try {
        // Always fetch fresh data to avoid stale hold settings
        const { data } = await axios.get(`${baseUrl}single`, {
          params: { name, market }
        })

        // Update in-memory array for other components that may reference it
        if (!this.restaurants) this.restaurants = []
        const existingIndex = this.restaurants.findIndex(r => r.name === name && r.market === market)
        if (existingIndex >= 0) {
          this.restaurants[existingIndex] = data
        } else {
          this.restaurants.push(data)
        }

        return data
      } catch (error) {
        console.error('Error fetching single restaurant:', error)
        reportError('restaurants.store.js:getSingleRestaurant', error.message, null, null, null, error)
        
        return null
      }
    },

    async getEventSpacesRestaurants(limit) {
      try {
        let res = await axios.get(`${baseUrl}event-spaces`, {
          params: {
            limit
          }
        })
        this.eventSpacesRestaurants = res.data
      } catch (error) {
        console.error('Error Event Spaces', error)
        reportError('restaurant.store.js:getEventSpacesRestaurants', error.message, null, null, null, error)
      }
    },

    async getHappeningsRestaurants(limit) {
      try {
        let res = await axios.get(`${baseUrl}happenings`, {
          params: {
            limit
          }
        })
        this.happeningsRestaurants = res.data
      } catch (error) {
        console.error('Error Happenings', error)
        reportError('restaurant.store.js:getHappeningsRestaurants', error.message, null, null, null, error)
      }
    },

    async getRestaurantById(restaurantId) {
      if (!restaurantId) {
        console.log('Error kkyd3')

        return
      }

      try {
        let res
        if (localStorage.getItem('jwt')) {
          res = await axios.get(`${baseUrl}${restaurantId}`, {
            params: {
            }
          })
        } else {
          // Unauthenticated fallback: fetch by profileId via public endpoint
          const storeRestaurant = this?.restaurants?.find(({ id, _id }) => id === restaurantId || _id === restaurantId)
          if (storeRestaurant?.profileId) {
            res = await axios.get(baseUrl, { params: { profileId: storeRestaurant.profileId } })

            // Public endpoint returns an array
            if (Array.isArray(res.data)) res.data = res.data[0]
          }
        }

        let theRestaurant
        if (res?.data) {
          theRestaurant = this?.restaurants?.find(({ id, _id }) => id === restaurantId || _id === restaurantId)
          if (theRestaurant) Object.assign(theRestaurant, res.data)
          else {
            theRestaurant = res.data
            this.restaurants.push(theRestaurant)
          }

          if (this.adminEditingRestaurant?.id == restaurantId) Object.assign(this.adminEditingRestaurant, res.data)
        }

        return theRestaurant
      } catch (error) {
        console.log('Error dffssd3', error)
        reportError('restaurant.store.js:getRestaurantById', error.message, null, null, null, error)
      }
    },

    async getRestaurantByGooglePlaceId(googlePlaceId) {
      if (googlePlaceId) {
        try {
          let res = await axios.get(`${baseUrl}`, {
            params: {
              googlePlaceId: googlePlaceId
            }
          })

          if (res.data?.[0]) {
            let theRestaurant = this?.restaurants?.find(({ _id }) => _id === res.data[0]._id)
            if (theRestaurant) {
              Object.assign(theRestaurant, res.data[0])
            } else {
              this.restaurants.push(res.data[0])
            }

            if (this.adminEditingRestaurant && this.adminEditingRestaurant._id == res.data[0]._id) Object.assign(this.adminEditingRestaurant, res.data[0])
            
            return res.data[0]
          }
        } catch (error) {
          console.log('Error dffddussd3', error)
          reportError('restaurant.store.js:getRestaurantBygooglePlaceId', error.message, null, null, null, error)
        }
      }
      else if (!googlePlaceId) {
        console.log('Error kksssyd3')
      }
    },

    async getRestaurantByProfileId(profileId) {
      if (profileId) {
        try {
          let res = await axios.get(`${baseUrl}`, {
            params: {
              profileId: profileId
            }
          })

          if (res.data?.[0]) {
            let theRestaurant = this?.restaurants?.find(({ _id }) => _id === res.data[0]._id)
            if (theRestaurant) {
              Object.assign(theRestaurant, res.data[0])
            } else {
              this.restaurants.push(res.data[0])
              theRestaurant = this.restaurants[this.restaurants.length - 1]
            }

            if (this.adminEditingRestaurant && this.adminEditingRestaurant._id == res.data[0]._id) Object.assign(this.adminEditingRestaurant, res.data[0])
            if (this.restaurantEditingRestaurant && this.restaurantEditingRestaurant._id == res.data[0]._id) Object.assign(this.restaurantEditingRestaurant, res.data[0])
            
            return theRestaurant
          }
        } catch (error) {
          console.log('Error dffssd3', error)
          reportError('restaurant.store.js:getRestaurantById', error.message, null, null, null, error)
        }
      }
      else if (!profileId) {
        console.log('Error kkyd3')
      }
    },

    async recordProfileView(restaurantId) {
      try {
        const { data } = await axios.post(`${baseUrl}${restaurantId}/view`)
        const storeRestaurant = this.restaurants.find(({ _id }) => _id === restaurantId)
        if (storeRestaurant) {
          storeRestaurant.profileViewCount = data.profileViewCount
        }
      } catch (error) {
        // Non-critical — don't block the user experience
      }
    },

    async adminEditRestaurant(idToEdit) {
      await this.getRestaurantById(idToEdit)
      this.adminEditingRestaurant = this.restaurants.find(({ _id }) => _id === idToEdit)

      // Apply default values for Unlisted restaurants (only if fields are empty)
      if (this.adminEditingRestaurant?.status === 'Unlisted') {
        const restaurant = this.adminEditingRestaurant

        // Default: Accepting Referrals - checked
        if (restaurant.acceptingReferrals !== true) {
          restaurant.acceptingReferrals = true
        }

        // Default: Team Identity for Reservation Referrals - "<Restaurant Name> Team"
        if (!restaurant.generalReservationTeamIdentity) {
          restaurant.generalReservationTeamIdentity = `${restaurant.name} Team`
        }

        // Default: Team Identity for Private Event Referrals - "<Restaurant Name> Team"
        if (!restaurant.privateReservationTeamIdentity) {
          restaurant.privateReservationTeamIdentity = `${restaurant.name} Team`
        }

        // Default: Required Credit Card Hold - checked
        if (!restaurant.generalReservationHoldRequired) {
          restaurant.generalReservationHoldRequired = true
        }

        // Extract domain from website URL for email defaults
        const extractDomain = websiteUrl => {
          if (!websiteUrl) return null
          try {
            const url = new URL(websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`)
            
            return url.hostname.replace(/^www\./, '')
          } catch {
            return null
          }
        }

        const domain = extractDomain(restaurant.website)

        if (domain) {
          // RM-189 — appends are [{ name, email }] entries; an empty array is
          // truthy, so guard on parsed length (handles legacy strings too).
          // Default: Emails to receive Reservation Referrals - info@<website domain>
          if (!parseContactEntries(restaurant.generalReservationEmailAppends, 'email').length) {
            restaurant.generalReservationEmailAppends = [{ name: '', email: `info@${domain}` }]
          }

          // Default: Emails to receive Private Event Requests - info@<website domain>, events@<website domain>
          if (!parseContactEntries(restaurant.privateReservationEmailAppends, 'email').length) {
            restaurant.privateReservationEmailAppends = [
              { name: '', email: `info@${domain}` },
              { name: '', email: `events@${domain}` },
            ]
          }
        }
      }

      this.adminEditingRestaurantPrevious = JSON.parse(JSON.stringify(this.adminEditingRestaurant))
    },

    //using restaurant here instead of idToEdit fixed reactivity issues in restaurant profile
    async restaurantEditRestaurant(restaurant, place) {
      await this.getRestaurantById(restaurant._id)
      this.restaurantEditingRestaurant = restaurant
      this.restaurantEditingRestaurant.googlePlaceData = place //rehydrate google place
      this.restaurantEditingRestaurantPrevious = JSON.parse(JSON.stringify(this.restaurantEditingRestaurant))
      this.restaurantEditingRestaurantPrevious.googlePlaceData = place //rehydrate google place
    },

    async adminEditRestaurantByGroup(groupId) {
      this.adminEditingRestaurant = this.restaurants.find(({ restaurantUserGroup }) => (restaurantUserGroup === groupId || restaurantUserGroup?._id === groupId))
      await this.getRestaurantById(this.adminEditingRestaurant._id)
      this.adminEditingRestaurantPrevious = JSON.parse(JSON.stringify(this.adminEditingRestaurant))
    },

    async cancelAdminEditRestaurant() {
      if (this.adminEditingRestaurant.isNew) {
        await this.adminDeleteRestaurant(this.adminEditingRestaurant.id)
      }
      if (this.adminEditingRestaurantPrevious && this.adminEditingRestaurant)
        Object.assign(this.adminEditingRestaurant, this.adminEditingRestaurantPrevious)
      this.adminEditingRestaurant = null
      this.adminEditingRestaurantPrevious = null
    },

    async cancelRestaurantEditRestaurant() {
      if (this.restaurantEditingRestaurantPrevious && this.restaurantEditingRestaurant)
        Object.assign(this.restaurantEditingRestaurant, this.restaurantEditingRestaurantPrevious)

      // TODO Given this data is supposedly only used for this edit, should we make sure to clear this data off?
      // if (this.restaurantEditingRestaurant) { // Ensure it exists before trying to delete a prop
      //   delete this.restaurantEditingRestaurant.googlePlaceData;
      // }
      this.restaurantEditingRestaurant = null
      this.restaurantEditingRestaurantPrevious = null
    },

    async saveAdminEditingRestaurant() {
      try {
        let res = await axios.put(`${baseUrl}${this.adminEditingRestaurant.id}`, this.adminEditingRestaurant, {
          params: {
          }
        })
        Object.assign(this.adminEditingRestaurant, res.data)

        let theRestaurant = this.restaurants.find(({ _id }) => _id === this.adminEditingRestaurant.id)
        if (theRestaurant)
          Object.assign(theRestaurant, res.data)

        if (this.adminEditingRestaurant.isNew)
          delete this.adminEditingRestaurant.isNew
        this.adminEditingRestaurant = null
        this.adminEditingRestaurantPrevious = null
      } catch (error) {
        console.error(error)

        // reportError('restaurant.store.js:saveAdminEditingRestaurant', error.message, null,null,null, error)

        return error.response.data
      }

    },

    async saveRestaurantEditingRestaurant() {
      try {
        let res = await axios.put(`${baseUrl}${this.restaurantEditingRestaurant.id}`, this.restaurantEditingRestaurant, {
          params: {
          }
        })
        Object.assign(this.restaurantEditingRestaurant, res.data)

        let theRestaurant = this.restaurants.find(({ _id }) => _id === this.restaurantEditingRestaurant._id)
        console.log('theRestaurant', theRestaurant)
        if (theRestaurant) {
          Object.assign(theRestaurant, res.data)

          // TODO Given this data is supposedly only used for this edit, should we make sure to clear this data off?
          // delete theRestaurant.googlePlaceData; // Clean up from the array instance
        }

        // TODO Given this data is supposedly only used for this edit, should we make sure to clear this data off?
        // if (this.restaurantEditingRestaurant) { // Ensure it exists
        //   delete this.restaurantEditingRestaurant.googlePlaceData; // Clean up from the editing instance
        // }

        this.restaurantEditingRestaurant = null
        this.restaurantEditingRestaurantPrevious = null
      } catch (error) {
        console.error(error)

        // reportError('restaurant.store.js:saveAdminEditingRestaurant', error.message, null,null,null, error)

        return error.response.data
      }
    },

    async updateEventSpaces(id, eventSpaces) {
      try {

        let theRestaurant = this.restaurants.find(({ _id }) => _id === id)
        theRestaurant.eventSpaces = eventSpaces
        let res = await axios.put(`${baseUrl}${id}`, theRestaurant, {
          params: {
          }
        })
      } catch (error) {
        console.error(error)

        // reportError('restaurant.store.js:saveAdminEditingRestaurant', error.message, null,null,null, error)

        return error.response.data
      }

    },

    async updateEventDescription(id, eventDescription) {
      try {

        let theRestaurant = this.restaurants.find(({ _id }) => _id === id)
        theRestaurant.eventDescription = eventDescription
        let res = await axios.put(`${baseUrl}${id}`, theRestaurant, {
          params: {
          }
        })
      } catch (error) {
        console.error(error)

        // reportError('restaurant.store.js:saveAdminEditingRestaurant', error.message, null,null,null, error)

        return error.response.data
      }

    },

    async adminDeleteRestaurant(idToDelete) {
      try {
        let res = await axios.delete(`${baseUrl}${idToDelete}`, {
          params: {
          }
        })

        //remove matching item from array
        var removeIndex = this.restaurants.map(item => item.id).indexOf(idToDelete)
        ~removeIndex && this.restaurants.splice(removeIndex, 1)

        if (this.adminEditingRestaurant?.id == idToDelete)
          this.adminEditingRestaurant = null

      } catch (error) {
        console.error(error)
        reportError('restaurant.store.js:adminDeleteRestaurant', error.message, null, null, null, error)
      }

    },

    async adminNewRestaurant(props) {
      //this.myReservation = { loading: true };
      try {
        let res = await axios.post(`${baseUrl}`, props || {}, {
          params: {
          }
        })
        let newRestaurant = res.data
        if (!props) {
          this.adminEditingRestaurant = newRestaurant
          this.adminEditingRestaurant.isNew = true
        }
        this.restaurants.push(newRestaurant)
        
        return newRestaurant
      } catch (error) {
        console.error(error)
        reportError('restaurant.store.js:adminNewRestaurant', error.message, null, null, null, error)
      }
    },

    async linkRestaurantToGooglePlace(restaurantId, googlePlaceDetails) {
      console.log('linkRestaurantToGooglePlace', restaurantId, googlePlaceDetails)

      // RM-157 dedup guard (run before any mutation): block linking a Google
      // Place that ANOTHER restaurant already owns. One place must map to a
      // single restaurant, so surface the conflict instead of silently
      // creating a duplicate link.
      const placeId = googlePlaceDetails.place_id
      const conflict = this.restaurants.find(r => r.googlePlaceId === placeId && r._id !== restaurantId)
      if (conflict) {
        return { error: `This Google Place is already linked to "${conflict.name}"${conflict.market ? ` (${conflict.market})` : ''}.` }
      }

      let domain = extractDomain(googlePlaceDetails.website)
      await this.adminEditRestaurant(restaurantId)
      this.adminEditingRestaurant.googlePlaceDetails = googlePlaceDetails
      this.adminEditingRestaurant.googlePlaceId = googlePlaceDetails.place_id
      this.adminEditingRestaurant.nameSuggestion = googlePlaceDetails.name

      // this.adminEditingRestaurant.marketSuggestion = formatMarket(googlePlaceDetails.address_components);
      this.adminEditingRestaurant.marketOld = this.adminEditingRestaurant.market
      this.adminEditingRestaurant.marketSecondary = this.adminEditingRestaurant.market
      this.adminEditingRestaurant.market = formatMarket(googlePlaceDetails.address_components)
      this.adminEditingRestaurant.website = googlePlaceDetails.website
      this.adminEditingRestaurant.address = googlePlaceDetails.vicinity
      this.adminEditingRestaurant.phoneNumber = googlePlaceDetails.formatted_phone_number
      this.adminEditingRestaurant.openingHours = googlePlaceDetails.opening_hours?.weekday_text
      this.adminEditingRestaurant.timeZoneIdentifier = googlePlaceDetails.timeZone.timeZoneId
      if (!this.adminEditingRestaurant.generalReservationTeamIdentity)
        this.adminEditingRestaurant.generalReservationTeamIdentity = googlePlaceDetails.name + ' Team'
      // RM-189 — appends are [{ name, email }] entries; guard on parsed length.
      if (!parseContactEntries(this.adminEditingRestaurant.generalReservationEmailAppends, 'email').length)
        this.adminEditingRestaurant.generalReservationEmailAppends = (domain ? [{ name: '', email: `reservations@${domain}` }, { name: '', email: `info@${domain}` }] : [])
      if (!this.adminEditingRestaurant.privateReservationTeamIdentity)
        this.adminEditingRestaurant.privateReservationTeamIdentity = googlePlaceDetails.name + ' Team'
      if (!parseContactEntries(this.adminEditingRestaurant.privateReservationEmailAppends, 'email').length)
        this.adminEditingRestaurant.privateReservationEmailAppends = (domain ? [{ name: '', email: `events@${domain}` }, { name: '', email: `info@${domain}` }] : [])

      // RM-157: persist immediately — one click links AND saves. Returns the
      // save error (if any) so the caller can surface it; undefined on success.
      return await this.saveAdminEditingRestaurant()
    },



    async newRestaurantFromGooglePlace(googlePlaceDetails, sourceEmail) {
      //console.log('newRestaurantFromGooglePlace', googlePlaceDetails)
      let domain = extractDomain(googlePlaceDetails.website)

      // await this.adminNewRestaurant()
      let newRestaurant = {}
      newRestaurant.googlePlaceDetails = googlePlaceDetails
      newRestaurant.googlePlaceId = googlePlaceDetails.place_id
      newRestaurant.name = googlePlaceDetails.name
      newRestaurant.market = formatMarket(googlePlaceDetails.address_components)
      newRestaurant.website = googlePlaceDetails.website
      newRestaurant.address = googlePlaceDetails.vicinity
      newRestaurant.phoneNumber = googlePlaceDetails.formatted_phone_number
      newRestaurant.openingHours = googlePlaceDetails.opening_hours?.weekday_text
      newRestaurant.timeZoneIdentifier = googlePlaceDetails.timeZone.timeZoneId
      newRestaurant.generalReservationTeamIdentity = googlePlaceDetails.name + ' Team'
      // RM-189 — appends are [{ name, email }] entries (name optional/internal).
      newRestaurant.generalReservationEmailAppends = (domain ? [{ name: '', email: `reservations@${domain}` }, { name: '', email: `info@${domain}` }] : [])

      newRestaurant.privateReservationTeamIdentity = googlePlaceDetails.name + ' Team'
      newRestaurant.privateReservationEmailAppends = (domain ? [{ name: '', email: `events@${domain}` }, { name: '', email: `info@${domain}` }] : [])

      newRestaurant.generalReservationHoldRequired = true
      newRestaurant.generalReservationHoldType = 'full'
      newRestaurant.generalReservationHoldTaxPercentage = 12
      newRestaurant.generalReservationHoldCancellationLimitHours = 24

      newRestaurant.profileId = 'auto'

      console.log('sourceEmail', sourceEmail)
      if (sourceEmail) {
        newRestaurant.sourceEmail = sourceEmail
        newRestaurant.sourceType = 'Member Favorite'
      }

      if (googlePlaceDetails.name && googlePlaceDetails.website) {
        newRestaurant.status = 'Member Favorite'
      } else {
        newRestaurant.status = 'Member Favorite'
      }
      
      return await this.adminNewRestaurant(newRestaurant)
    },

    async favoriteRestaurant(restaurantId, userId) {
      try {
        let res = await axios.put(`${baseUrl}${restaurantId}`, { addFavorite: true }, {
          params: {
          }
        })

        let theRestaurant = this.restaurants.find(r => r._id === restaurantId || r.id === restaurantId)
        if (theRestaurant) {
          Object.assign(theRestaurant, res.data)
          theRestaurant.isFavorited = true
        }

        // Also update in search results for immediate UI feedback
        let searchRestaurant = this.restaurantSearchResults.find(r => r._id === restaurantId || r.id === restaurantId)
        if (searchRestaurant) {
          Object.assign(searchRestaurant, res.data)
          searchRestaurant.isFavorited = true
        }

        // Also update in bookedRestaurantsSearch
        let bookedSearchRestaurant = this.bookedRestaurantsSearch.find(r => (r.raw?._id === restaurantId || r.raw?.id === restaurantId))
        if (bookedSearchRestaurant?.raw) {
          Object.assign(bookedSearchRestaurant.raw, res.data)
          bookedSearchRestaurant.raw.isFavorited = true
        }

        // Also update in bookedRestaurants (used by RestaurantGridCard on discover page)
        this.bookedRestaurants.forEach(item => {
          if (item.raw?._id === restaurantId || item.raw?.id === restaurantId) {
            Object.assign(item.raw, res.data)
            item.raw.isFavorited = true
            delete item.raw.unfavorited
          }
        })

        // Also update in preferredRestaurants
        let preferredRestaurant = this.preferredRestaurants.find(r => r._id === restaurantId || r.id === restaurantId)
        if (preferredRestaurant) {
          Object.assign(preferredRestaurant, res.data)
          preferredRestaurant.isFavorited = true
        }

        // Also update in favoritedRestaurants
        let favRestaurant = this.favoritedRestaurants.find(r => r._id === restaurantId || r.id === restaurantId)
        if (favRestaurant) {
          Object.assign(favRestaurant, res.data)
          favRestaurant.isFavorited = true
        }

        // Also update in friendsFavoriteRestaurants
        let friendsFavRestaurant = this.friendsFavoriteRestaurants.find(r => r._id === restaurantId || r.id === restaurantId)
        if (friendsFavRestaurant) {
          Object.assign(friendsFavRestaurant, res.data)
          friendsFavRestaurant.isFavorited = true
        }

        // Add to myUser.favoritedRestaurants so favorites list updates immediately
        const usersStore = useUsersStore()
        const myUser = usersStore.myUser
        if (myUser?.favoritedRestaurants && !myUser.favoritedRestaurants.includes(restaurantId)) {
          myUser.favoritedRestaurants.push(restaurantId)
          myUser.favoritedRestaurantsBookingCount?.push(null)
          myUser.favoritedRestaurantsLastBookingDate?.push(null)
          myUser.favoritedRestaurantsLastSubmittedDate?.push(null)
          // RM-296: stamp it now so the new favorite is top of My Favorites → Recent
          // straight away, without waiting for the next favorites-data fetch.
          myUser.favoritedAt = { ...(myUser.favoritedAt || {}), [restaurantId]: new Date().toISOString() }
          if (myUser.favoritedRestaurantsCount != null) {
            myUser.favoritedRestaurantsCount++
          }
        }
      } catch (error) {
        console.error(error)
        reportError('restaurant.store.js:favoriteRestaurant', error.message, null, null, null, error)
      }
    },
    async unfavoriteRestaurant(restaurantId, userId) {
      try {
        let res = await axios.put(`${baseUrl}${restaurantId}`, { removeFavorite: true }, {
          params: {
          }
        })

        let theRestaurant = this.restaurants.find(r => r._id === restaurantId || r.id === restaurantId)

        if (theRestaurant) {
          Object.assign(theRestaurant, res.data)
          theRestaurant.isFavorited = false
          theRestaurant.unfavorited = true
        }

        // Also update in search results for immediate UI feedback
        let searchRestaurant = this.restaurantSearchResults.find(r => r._id === restaurantId || r.id === restaurantId)
        if (searchRestaurant) {
          Object.assign(searchRestaurant, res.data)
          searchRestaurant.isFavorited = false
          searchRestaurant.unfavorited = true
        }

        // Also update in bookedRestaurantsSearch
        let bookedSearchRestaurant = this.bookedRestaurantsSearch.find(r => (r.raw?._id === restaurantId || r.raw?.id === restaurantId))
        if (bookedSearchRestaurant?.raw) {
          Object.assign(bookedSearchRestaurant.raw, res.data)
          bookedSearchRestaurant.raw.isFavorited = false
          bookedSearchRestaurant.raw.unfavorited = true
        }

        // Also update in bookedRestaurants (used by RestaurantGridCard on discover page)
        this.bookedRestaurants.forEach(item => {
          if (item.raw?._id === restaurantId || item.raw?.id === restaurantId) {
            Object.assign(item.raw, res.data)
            item.raw.isFavorited = false
            item.raw.unfavorited = true
          }
        })

        // Also update in preferredRestaurants
        let preferredRestaurant = this.preferredRestaurants.find(r => r._id === restaurantId || r.id === restaurantId)
        if (preferredRestaurant) {
          Object.assign(preferredRestaurant, res.data)
          preferredRestaurant.isFavorited = false
        }

        // Also update in favoritedRestaurants
        let favRestaurant = this.favoritedRestaurants.find(r => r._id === restaurantId || r.id === restaurantId)
        if (favRestaurant) {
          Object.assign(favRestaurant, res.data)
          favRestaurant.isFavorited = false
        }

        // Also update in friendsFavoriteRestaurants
        let friendsFavRestaurant = this.friendsFavoriteRestaurants.find(r => r._id === restaurantId || r.id === restaurantId)
        if (friendsFavRestaurant) {
          Object.assign(friendsFavRestaurant, res.data)
          friendsFavRestaurant.isFavorited = false
        }

        // Remove from myUser.favoritedRestaurants so favorites list updates immediately
        const usersStore = useUsersStore()
        const myUser = usersStore.myUser
        if (myUser?.favoritedRestaurants) {
          const idx = myUser.favoritedRestaurants.indexOf(restaurantId)
          if (idx !== -1) {
            myUser.favoritedRestaurants.splice(idx, 1)
            myUser.favoritedRestaurantsBookingCount?.splice(idx, 1)
            myUser.favoritedRestaurantsLastBookingDate?.splice(idx, 1)
            myUser.favoritedRestaurantsLastSubmittedDate?.splice(idx, 1)
            if (myUser.favoritedAt) delete myUser.favoritedAt[restaurantId]
            if (myUser.favoritedRestaurantsCount != null) {
              myUser.favoritedRestaurantsCount--
            }
          }
        }

        // Removing a favorite NEVER removes what the member wrote (RM-294):
        // the Dining Profile entry survives, so the note stays on the
        // restaurant page and moves into Favorites → Notes, where entries
        // about non-favorited restaurants live. Refresh that feed so it shows
        // up there without a reload.
        this.refreshUnfavoritedNotes()
      } catch (error) {
        console.error(error)
        reportError('restaurant.store.js:unfavoriteRestaurant', error.message, null, null, null, error)
      }
    },

    initFavoriteNotes(notesMap) {
      this.favoriteNotes = notesMap || {}
    },

    // Re-pull the member's notes from the server. The store map is seeded once
    // at login (/user/me), but notes can also change server-side — the RM AI
    // saves them via its own tools — so surfaces that DISPLAY a note (e.g. the
    // restaurant profile) refresh on open instead of trusting the stale map.
    async refreshFavoriteNotes() {
      try {
        const { data } = await axios.get(`${baseUrl}favorite-notes`)
        this.favoriteNotes = data || {}
      } catch (error) {
        console.error('Error refreshing favorite notes:', error)
      }
    },

    /**
     * Mirror a favorite the SERVER already made (RM AI's favorite_restaurant, or
     * add_to_profile with favorite: true) into the same local caches the UI toggle
     * patches. Founder review, 2026-09-17: without this the chat row, the heart and
     * the restaurant page stayed stale until the member reloaded.
     */
    applyFavoriteLocally(restaurantId, favorited) {
      if (!restaurantId) return
      const id = String(restaurantId)
      const lists = [this.restaurants, this.favoritedRestaurants, this.friendsFavoriteRestaurants, this.preferredRestaurants]
      for (const list of lists) {
        const match = (list || []).find(r => String(r?._id) === id || String(r?.id) === id)
        if (match) {
          match.isFavorited = favorited
          if (favorited) delete match.unfavorited
        }
      }
      for (const item of this.bookedRestaurants || []) {
        if (item.raw && (String(item.raw._id) === id || String(item.raw.id) === id)) {
          item.raw.isFavorited = favorited
          if (favorited) delete item.raw.unfavorited
        }
      }

      const usersStore = useUsersStore()
      const myUser = usersStore.myUser
      if (!myUser?.favoritedRestaurants) return
      const has = myUser.favoritedRestaurants.some(r => String(r?._id || r) === id)
      if (favorited && !has) {
        myUser.favoritedRestaurants.push(restaurantId)
        myUser.favoritedRestaurantsBookingCount?.push(null)
        myUser.favoritedRestaurantsLastBookingDate?.push(null)
        myUser.favoritedRestaurantsLastSubmittedDate?.push(null)
        myUser.favoritedAt = { ...(myUser.favoritedAt || {}), [id]: new Date().toISOString() }
        if (myUser.favoritedRestaurantsCount != null) myUser.favoritedRestaurantsCount++
      } else if (!favorited && has) {
        const idx = myUser.favoritedRestaurants.findIndex(r => String(r?._id || r) === id)
        if (idx !== -1) {
          myUser.favoritedRestaurants.splice(idx, 1)
          myUser.favoritedRestaurantsBookingCount?.splice(idx, 1)
          myUser.favoritedRestaurantsLastBookingDate?.splice(idx, 1)
          myUser.favoritedRestaurantsLastSubmittedDate?.splice(idx, 1)
          if (myUser.favoritedAt) delete myUser.favoritedAt[id]
          if (myUser.favoritedRestaurantsCount != null) myUser.favoritedRestaurantsCount--
        }
      }
    },

    async refreshUnfavoritedNotes() {
      try {
        const { data } = await axios.get(`${baseUrl}unfavorited-notes`)
        this.unfavoritedNotes = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('Error refreshing restaurant notes:', error)
      }
    },

    async saveFavoriteNote(restaurantId, note) {
      try {
        const { data } = await axios.put(`${baseUrl}${restaurantId}/favorite-note`, { note })
        if (data.deleted) {
          delete this.favoriteNotes[restaurantId]
        } else {
          // Keep `count` — the row reads it for "+N more notes", and saving adds an entry.
          this.favoriteNotes[restaurantId] = {
            note: data.note,
            updatedAt: data.updatedAt,
            count: data.count ?? ((this.favoriteNotes[restaurantId]?.count || 0) + 1),
            entryId: data.entryId,
          }
        }

        return { ok: true }
      } catch (error) {
        console.error('Error saving favorite note:', error)
        reportError('restaurant.store.js:saveFavoriteNote', error.message, null, null, null, error)

        // The member has to be told. Clearing the box and pressing Save used to
        // close the dialog with nothing saved, no error and no hint that
        // removing words is a delete — it looked like the note had gone
        // (RM-301 hand test). The server's own sentence is the right one:
        // "Nothing to save. To remove a note, delete that note."
        return { ok: false, message: error?.response?.data?.message || 'That note could not be saved. Please try again.' }
      }
    },

    async deleteFavoriteNote(restaurantId) {
      try {
        await axios.delete(`${baseUrl}${restaurantId}/favorite-note`)
        delete this.favoriteNotes[restaurantId]
      } catch (error) {
        console.error('Error deleting favorite note:', error)
        reportError('restaurant.store.js:deleteFavoriteNote', error.message, null, null, null, error)
      }
    },

    async getFavoritedBy(restaurantId) {
      try {
        const { data } = await axios.get(`${baseUrl}${restaurantId}/favorited-by`)

        return data
      } catch (error) {
        console.error('Error fetching favorited-by:', error)
        reportError('restaurant.store.js:getFavoritedBy', error.message, null, null, null, error)

        return []
      }
    },

    async checkExistingPlaces(placeIds) {
      try {
        const { data } = await axios.post(`${baseUrl}check-existing-places`, { placeIds })
        
        return data // Returns map of placeId -> restaurant data
      } catch (error) {
        console.error('Error checking existing places:', error)
        reportError('restaurants.store.js:checkExistingPlaces', error.message, null, null, null, error)
        
        return {}
      }
    },

  }
})
